// Filename: EnrollmentApprovalModal.js
import { CheckCircleOutlined, CloseCircleOutlined, EyeOutlined, FileOutlined, SolutionOutlined } from "@ant-design/icons";
import {
    Button, Card, Collapse, Descriptions, Divider, Empty, Input, List, Modal, Space, Spin, Tabs, Tag, Tooltip, Typography, message, notification, Alert, Select,
} from "antd";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import Swal from 'sweetalert2';

import { getApplicantByEmailApiRequest } from '../../../api/applicant';

import EnrollmentInfo from './EnrollmentInfo';
import PersonalInfo from './PersonalInfo';
import StatusInfo from './StatusInfo';

import { STATUS_COLORS } from "./utils";

const { Title, Text } = Typography;
const { Panel } = Collapse;

const StatusTag = memo(({ status }) => {
    const color = STATUS_COLORS[status?.toLowerCase()] || "default";
    return <Tag color={color}>{String(status || 'N/A').toUpperCase()}</Tag>;
});


const EnrollmentApprovalModal = ({ visible, applicant, onClose, onStatusUpdate }) => {
    const [updateLoading, setUpdateLoading] = useState(false);

    const [fetchingDetails, setFetchingDetails] = useState(false);
    const [applicantDetails, setApplicantDetails] = useState(null);
    const [assignedSection, setAssignedSection] = useState(null);

    const [availableSections, setAvailableSections] = useState([]);
    const [sectionsLoading, setSectionsLoading] = useState(false);

    useEffect(() => {
        const fetchDetails = async (email) => {
            if (!email) return;

            setFetchingDetails(true);
            try {
                const response = await getApplicantByEmailApiRequest(email);
                if (response.success && response.data) {
                    setApplicantDetails(response.data);
                } else {
                    message.error(response.message || 'Failed to fetch applicant details');
                    setApplicantDetails(null);
                }
            } catch (error) {
                console.error('Error fetching applicant details for modal:', error);
                message.error('An error occurred while fetching applicant details.');
                setApplicantDetails(null);
            } finally {
                setFetchingDetails(false);
            }
        };

        if (visible && applicant?.email) {
            fetchDetails(applicant.email);
        }

        if (!visible) {
            setApplicantDetails(null);
            setAssignedSection(null);
            setAvailableSections([]);
        }
    }, [visible, applicant]);

    useEffect(() => {
        const fetchSectionsForApplicant = async (applicantStrand, applicantGradeLevel) => {
            if (!applicantStrand || !applicantGradeLevel) return;

            setSectionsLoading(true);
            setAvailableSections([]);
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
                const response = await fetch(`${apiUrl}/api/admin/sections?strand=${applicantStrand}&gradeLevel=${applicantGradeLevel}&status=Active`);
                const result = await response.json();

                if (response.ok && result.data) {
                    setAvailableSections(result.data);
                } else {
                    message.error(result.message || 'Failed to fetch available sections.');
                }
            } catch (error) {
                console.error("Error fetching sections:", error);
                message.error("An error occurred while fetching sections.");
            } finally {
                setSectionsLoading(false);
            }
        };

        if (applicantDetails?.academicStrand && applicantDetails?.academicLevel) {
            fetchSectionsForApplicant(applicantDetails.academicStrand, applicantDetails.academicLevel);
        }
    }, [applicantDetails]);


    const handleActualAction = useCallback(async (newStatus, reason = null) => {
        setUpdateLoading(true);
        try {
            const dataForUpdate = {
                assignedSection,
                ...(reason && { rejectionReason: reason }),
            };
            await onStatusUpdate(applicant, newStatus, dataForUpdate);
        } catch (error) {
            notification.error({
                message: "Update Failed",
                description: error.message || "An unexpected error occurred.",
            });
        } finally {
            setUpdateLoading(false);
        }
    }, [applicant, onStatusUpdate, assignedSection]);

    const showConfirmAction = useCallback(async (newStatus) => {
        if (newStatus === "Approved" && !assignedSection) {
            notification.warning({
                message: "Section Not Assigned",
                description: "Please assign the applicant to a section before approving the enrollment.",
            });
            return;
        }

        const selectedSectionLabel = availableSections.find(opt => opt._id === assignedSection)?.sectionName;

        let confirmConfig = {
            title: `Confirm Enrollment ${newStatus}`,
            html: `
                <p>You are about to <strong>${newStatus.toUpperCase()}</strong> this applicant's enrollment.</p>
                ${newStatus === "Approved" ? `<p>They will be assigned to section: <strong>${selectedSectionLabel}</strong>.</p>` : ''}
                <p style="color: #dc3545; font-weight: bold; margin-top: 12px;">
                    This action will officially finalize their enrollment status. Are you sure?
                </p>
            `,
            icon: newStatus === "Rejected" ? 'warning' : 'question',
            showCancelButton: true,
            confirmButtonText: `Yes, ${newStatus}`,
            cancelButtonText: 'No, Cancel',
            confirmButtonColor: newStatus === "Rejected" ? '#dc3545' : '#52c41a',
            cancelButtonColor: '#6c757d',
        };

        if (newStatus === "Rejected") {
            confirmConfig.input = 'textarea';
            confirmConfig.inputPlaceholder = 'Please provide a clear reason for the rejection...';
            confirmConfig.inputValidator = (value) => {
                if (!value || value.trim().length < 10) {
                    return 'Rejection reason must be at least 10 characters.';
                }
            };
        }

        const result = await Swal.fire(confirmConfig);
        if (result.isConfirmed) {
            const reason = newStatus === "Rejected" ? result.value : null;
            handleActualAction(newStatus, reason);
        }
    }, [handleActualAction, assignedSection, availableSections]);

    const modalFooter = useMemo(() => {
        if (!applicantDetails) {
            return [<Button key="close" onClick={onClose}>Close</Button>];
        }

        const isAlreadyProcessed = ['Approved', 'Rejected'].includes(applicantDetails.enrollmentApprovalAdminStatus);
        const isApproveDisabled = isAlreadyProcessed || !assignedSection;

        return [
            <Space key="actions" size="middle" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button key="reject" danger icon={<CloseCircleOutlined />} onClick={() => showConfirmAction("Rejected")} loading={updateLoading} disabled={isAlreadyProcessed} size="large">
                    Reject Enrollment
                </Button>
                <Tooltip title={isApproveDisabled && !isAlreadyProcessed ? "Please assign a section before approving." : ""}>
                    <span> {/* Tooltip needs a span wrapper for disabled buttons */}
                        <Button 
                            key="approve" 
                            type="primary" 
                            icon={<CheckCircleOutlined />} 
                            onClick={() => showConfirmAction("Approved")} 
                            loading={updateLoading} 
                            disabled={isApproveDisabled} 
                            style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }} 
                            size="large"
                        >
                            Approve Enrollment
                        </Button>
                    </span>
                </Tooltip>
                <Button key="close" onClick={onClose} size="large" disabled={updateLoading}>
                    Close
                </Button>
            </Space>,
        ];
    }, [showConfirmAction, updateLoading, applicantDetails, onClose, assignedSection]);

    const detailTabItems = useMemo(() => [
        { key: '1', label: 'Enrollment Info', children: applicantDetails ? <EnrollmentInfo data={applicantDetails} /> : <Spin /> },
        { key: '2', label: 'Applicant Info', children: applicantDetails ? <PersonalInfo data={applicantDetails} /> : <Spin /> },
        { key: '3', label: 'Status Summary', children: applicantDetails ? <StatusInfo data={applicantDetails} /> : <Spin /> },
    ], [applicantDetails]);

    const displayApplicant = applicantDetails || applicant;

    const renderRequirementList = (title, dataSource) => {
        if (!dataSource || dataSource.length === 0) {
            return (
                <>
                    <Title level={5}>{title}</Title>
                    <Empty description={`No ${title.toLowerCase()} have been submitted.`} />
                </>
            );
        }

        return (
            <>
                <Title level={5}>{title}</Title>
                <List
                    itemLayout="horizontal"
                    dataSource={dataSource}
                    renderItem={(item, index) => {
                        const viewableUrl = item.imageUrl || item.fileUrl || item.fileViewUrl;
                        const isWaived = item.status?.toLowerCase() === 'waived';
                        const hasViewableLink = !!viewableUrl;

                        return (
                            <List.Item
                                key={item.fileName || index}
                                actions={
                                    !isWaived && hasViewableLink
                                        ? [<Tooltip title="View File" key="view-tooltip"><Button type="text" icon={<EyeOutlined />} onClick={() => window.open(viewableUrl, '_blank')} /></Tooltip>]
                                        : null
                                }
                            >
                                <List.Item.Meta
                                    avatar={<FileOutlined style={{ fontSize: '20px', color: '#1890ff' }} />}
                                    title={
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {!isWaived && hasViewableLink ? (<a href={viewableUrl} target="_blank" rel="noopener noreferrer">{item.fileName || 'View Submitted File'}</a>) : (<span>{item.fileName || 'Requirement File'}</span>)}
                                            <StatusTag status={item.status} />
                                        </div>
                                    }
                                    description={`Requirement: ${item.name || 'N/A'}`}
                                />
                            </List.Item>
                        );
                    }}
                />
            </>
        );
    };

    return (
        <>
            <Modal
                title={<div className="flex items-center"><SolutionOutlined className="mr-2 text-blue-500" /><span className="text-lg font-medium">Enrollment Approval Review</span></div>}
                open={visible}
                onCancel={onClose}
                footer={modalFooter}
                width={1100}
                centered
                destroyOnClose
            >
                <div className="p-4">
                    <Spin spinning={fetchingDetails} tip="Loading Full Applicant Details...">
                        <Card className="mb-5 border-l-4 shadow-sm" style={{ borderLeftColor: "#1890ff" }}>
                            <div className="text-center mb-2">
                                <Title level={4} style={{ margin: 0, color: "#1890ff" }}>
                                    {displayApplicant?.firstName} {displayApplicant?.middleName} {displayApplicant?.lastName}
                                </Title>
                                <Tag color="blue" className="text-base px-4 py-1 mt-2">
                                    {displayApplicant?.applicantID}
                                </Tag>
                            </div>
                        </Card>

                        {applicantDetails ? (
                            <>
                                <Descriptions title={<Title level={5}>Enrollment Information (Current Application)</Title>} bordered size="middle" column={2} className="mb-5" labelStyle={{ fontWeight: "bold", width: '30%' }}>
                                    <Descriptions.Item label="Academic Level (Applying for)">{applicantDetails.academicLevel || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Academic Strand (Applying for)">{applicantDetails.academicStrand || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Academic Year (Applying for)">{applicantDetails.academicYear || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Academic Term (Applying for)">{applicantDetails.academicTerm || 'N/A'}</Descriptions.Item>
                                </Descriptions>
                                <Descriptions title={<Title level={5}>Enrollment Checklist</Title>} bordered size="middle" column={2} className="mb-5" labelStyle={{ fontWeight: "bold", width: '30%' }}>
                                    <Descriptions.Item label="Foreign Student"><Text strong>{applicantDetails.isForeignStudent ? 'Yes' : 'No'}</Text></Descriptions.Item>
                                    <Descriptions.Item label="Enrollment Requirements Status"><StatusTag status={applicantDetails.enrollmentRequirementsStatus} /></Descriptions.Item>
                                    <Descriptions.Item label="Voucher Type"><StatusTag status={applicantDetails.voucherType || 'N/A'} /></Descriptions.Item>
                                    <Descriptions.Item label="Voucher Application Status"><StatusTag status={applicantDetails.voucherApplicationStatus} /></Descriptions.Item>
                                </Descriptions>

                                <Divider />

                                <Descriptions title={<Title level={5}>Final Enrollment Assignment</Title>} bordered size="middle" column={1} className="mb-5" labelStyle={{ fontWeight: "bold", width: '30%' }}>
                                    <Descriptions.Item label="Assign Section">
                                        <Select
                                            value={assignedSection}
                                            onChange={setAssignedSection}
                                            loading={sectionsLoading}
                                            style={{ width: '100%' }}
                                            placeholder="Select a section for the applicant (Required for Approval)"
                                            disabled={['Approved', 'Rejected'].includes(applicantDetails.enrollmentApprovalAdminStatus) || updateLoading || sectionsLoading}
                                        >
                                            {availableSections.map(section => (
                                                <Select.Option key={section._id} value={section._id}>
                                                    {section.sectionName}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                        <Text type="secondary" style={{display: 'block', marginTop: '8px'}}>
                                            This initial section is for assessment purposes and may be subject to change upon final enrollment.
                                        </Text>
                                    </Descriptions.Item>
                                </Descriptions>

                                <Divider />

                                {renderRequirementList("Submitted Enrollment Requirements", applicantDetails.enrollmentRequirements)}

                                <Divider />

                                {applicantDetails.voucherType === 'PEAC Voucher' && (
                                    <Alert
                                        message="PEAC Voucher Holder"
                                        description="This applicant has a PEAC voucher. Please verify their submitted voucher requirements."
                                        type="info"
                                        showIcon
                                        style={{ marginBottom: '16px' }}
                                    />
                                )}

                                {applicantDetails.voucherType !== 'PEAC Voucher' && (
                                    <Alert
                                        message="Note: No Voucher Requirements Needed"
                                        description="This applicant does not have a PEAC voucher, so no voucher requirements are necessary for their enrollment."
                                        type="warning"
                                        showIcon
                                        style={{ marginBottom: '16px' }}
                                    />
                                )}


                                {renderRequirementList("Submitted Voucher Requirements", applicantDetails.voucherRequirements)}

                                <Divider />

                                <Collapse ghost>
                                    <Panel header={<Title level={5} style={{ margin: 0 }}>View Full Applicant Details</Title>} key="1">
                                        <Tabs defaultActiveKey="1" items={detailTabItems} type="card" />
                                    </Panel>
                                </Collapse>

                            </>
                        ) : (
                            <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {fetchingDetails ? null : <Text type="secondary">Could not load details.</Text>}
                            </div>
                        )}
                    </Spin>
                </div>
            </Modal>
        </>
    );
};

export default memo(EnrollmentApprovalModal);